/**
 * useChatSidebar — React-context store for the chat message buffer +
 * active threadId, shared between the full-page `/portal/chat` surface
 * and the `<TrackerSidebar>` Conversations list.
 *
 * Wave-10 (2026-05-24): the drawer chat is gone. This hook used to own
 * open/close state, a Cmd-K toggle, and a `pendingPrompt` rendezvous;
 * all of that is gone. The remaining surface is just the message buffer
 * + active thread, which is still legitimately shared between the chat
 * page (writes from `useChatStream`'s callbacks) and the sidebar
 * (reads to highlight the active conversation row).
 *
 * External prompt prefill is no longer routed through this context —
 * callers `navigate({ to: "/portal/chat", search: { seedPrompt, … } })`
 * and the chat page consumes the search params. See
 * `/tmp/design-handoff-v6/carbon-clear/project/row-explain-chat.jsx`
 * for the Wave-10 design.
 *
 * The provider is mounted at the root layout (`routes/__root.tsx`).
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";

import type { ChatMessageView } from "@/pages/Staff/Chat/types";

/**
 * Shared localStorage key for the active chat threadId.
 *
 * Reads/writes by `useChatSidebar` keep the active thread available to
 * the chat page's mount-time rehydration path even across full page
 * reloads. The TrackerSidebar's "active conversation" highlight reads
 * from the route URL (not this key) so the two systems stay in sync
 * with what the user is actually looking at.
 */
const ACTIVE_THREAD_LS_KEY = "carbonclear.chat.activeThreadId";

const readThreadFromLS = (): string | undefined => {
  if (typeof window === "undefined") return undefined;
  try {
    const v = window.localStorage.getItem(ACTIVE_THREAD_LS_KEY);
    return v && v.length > 0 ? v : undefined;
  } catch {
    return undefined;
  }
};

const writeThreadToLS = (threadId: string | undefined): void => {
  if (typeof window === "undefined") return;
  try {
    if (threadId) {
      window.localStorage.setItem(ACTIVE_THREAD_LS_KEY, threadId);
    } else {
      window.localStorage.removeItem(ACTIVE_THREAD_LS_KEY);
    }
  } catch {
    // Quota exceeded / SecurityError / private mode — silent.
  }
};

interface ChatSidebarContextValue {
  messages: ChatMessageView[];
  setMessages: Dispatch<SetStateAction<ChatMessageView[]>>;
  threadId: string | undefined;
  setThreadId: (id: string | undefined) => void;
}

const ChatSidebarContext = createContext<ChatSidebarContextValue | null>(null);

export const ChatSidebarProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [messages, setMessages] = useState<ChatMessageView[]>([]);
  // Initialize from localStorage so a page reload restores the
  // last-active thread.
  const [threadId, setThreadIdState] = useState<string | undefined>(() =>
    readThreadFromLS(),
  );

  const setThreadId = useCallback((id: string | undefined) => {
    setThreadIdState(id);
    writeThreadToLS(id);
  }, []);

  // Belt-and-suspenders: defensive sync on any path that bypasses
  // setThreadId (none today, but guards against future contributors
  // calling setThreadIdState directly via the context).
  useEffect(() => {
    writeThreadToLS(threadId);
  }, [threadId]);

  const value = useMemo<ChatSidebarContextValue>(
    () => ({
      messages,
      setMessages,
      threadId,
      setThreadId,
    }),
    [messages, threadId, setThreadId],
  );

  return (
    <ChatSidebarContext.Provider value={value}>
      {children}
    </ChatSidebarContext.Provider>
  );
};

/**
 * Read/write hook for the global chat-thread context.
 *
 * Throws when called outside `<ChatSidebarProvider>` — keeps mistakes
 * loud in dev rather than silently no-op'ing.
 */
export const useChatSidebar = (): ChatSidebarContextValue => {
  const ctx = useContext(ChatSidebarContext);
  if (!ctx) {
    throw new Error(
      "useChatSidebar must be used inside <ChatSidebarProvider>. " +
        "Mount the provider at the root layout.",
    );
  }
  return ctx;
};

/**
 * Soft-fail variant — returns ``null`` outside the provider rather
 * than throwing. Used by components that CAN render without the
 * chat context (e.g. SchedulingFollowUpPills on isolated unit-test
 * renders): the component degrades to a no-op instead of crashing.
 *
 * Most callers should use {@link useChatSidebar} — the loud-throw
 * variant — so misconfigured providers fail at first render in dev.
 */
export const useChatSidebarOptional = (): ChatSidebarContextValue | null => {
  return useContext(ChatSidebarContext);
};
